/**
 * Application shell. Desktop (>800px): side nav + centred content (max 1200px).
 * Mobile: full-width content + bottom nav + More sheet — the Flutter owner module's
 * `OpScaffold` one-screen-at-a-time layout.
 */
import { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { appRoutes } from '../routes';
import { useIsDesktop } from '../hooks/useMediaQuery';
import SideNav, { SIDE_NAV_COLLAPSED, SIDE_NAV_EXPANDED } from '../widgets/layout/side-nav';
import BottomNav, { BOTTOM_NAV_HEIGHT } from '../widgets/layout/bottom-nav';
import MoreSheet from '../widgets/layout/more-sheet';
import OutletPicker from '../widgets/layout/outlet-picker';
import PageLoader from '../components/ui/page-loader';

const COLLAPSE_KEY = 'resto.sidenav.collapsed';

export default function OwnerLayout() {
    const isDesktop = useIsDesktop();
    const [moreOpen, setMoreOpen] = useState(false);
    const [outletPickerOpen, setOutletPickerOpen] = useState(false);
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(COLLAPSE_KEY) === '1';
        } catch {
            return false;
        }
    });

    const toggleCollapsed = () =>
        setCollapsed((c) => {
            try {
                localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
            } catch {
                /* private mode */
            }
            return !c;
        });

    // Close the More sheet whenever we switch to desktop.
    useEffect(() => {
        if (isDesktop) setMoreOpen(false);
    }, [isDesktop]);

    return (
        <div className="min-h-screen bg-bg">
            {isDesktop && (
                <SideNav
                    collapsed={collapsed}
                    onToggleCollapsed={toggleCollapsed}
                    onPickOutlet={() => setOutletPickerOpen(true)}
                />
            )}

            <div
                className="min-w-0 overflow-x-hidden transition-[margin] duration-200"
                style={
                    isDesktop
                        ? { marginLeft: collapsed ? SIDE_NAV_COLLAPSED : SIDE_NAV_EXPANDED }
                        : { paddingBottom: BOTTOM_NAV_HEIGHT + 8 }
                }
            >
                <div className="mx-auto w-full max-w-[1200px] px-3 pb-8 pt-3 md:px-5">
                    <Suspense fallback={<PageLoader />}>
                        <Routes>
                            {appRoutes.map(({ path, element: Element }) => (
                                <Route key={path} path={path} element={<Element />} />
                            ))}
                            <Route path="*" element={<Navigate to="/owner/dashboard" replace />} />
                        </Routes>
                    </Suspense>
                </div>
            </div>

            {!isDesktop && <BottomNav moreOpen={moreOpen} onOpenMore={() => setMoreOpen(true)} />}
            {!isDesktop && (
                <MoreSheet
                    open={moreOpen}
                    onClose={() => setMoreOpen(false)}
                    onPickOutlet={() => setOutletPickerOpen(true)}
                />
            )}
            <OutletPicker open={outletPickerOpen} onClose={() => setOutletPickerOpen(false)} />
        </div>
    );
}
